import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import CopyButton from './CopyButton';

const TOOLTIP_CLOSE_DELAY_MS = 200;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_MAX_HEIGHT = 240;
const TOOLTIP_GAP = 8;
const VIEWPORT_PAD = 16;

let nextTooltipOwnerId = 1;
let activeTooltipOwnerId = null;

function TruncatedCopyCell({
  value = '',
  displayText,
  copyAriaLabel,
  textAs,
  tooltipWrap = false,
  textClassName = '',
}) {
  const hasValue = Boolean(value);
  const renderedText = displayText ?? (hasValue ? value : '-');
  const isTruncated = hasValue && renderedText !== value;

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const ownerIdRef = useRef(null);
  if (ownerIdRef.current === null) {
    ownerIdRef.current = nextTooltipOwnerId;
    nextTooltipOwnerId += 1;
  }

  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTimerRef = useRef(null);

  const isWithinHoverArea = useCallback((node) => {
    if (!node || node.nodeType !== 1) return false;
    return Boolean(
      wrapperRef.current?.contains(node)
      || tooltipRef.current?.contains(node)
    );
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const releaseTooltip = useCallback(() => {
    if (activeTooltipOwnerId === ownerIdRef.current) {
      activeTooltipOwnerId = null;
    }
    setTooltipOpen(false);
  }, []);

  const updateTooltipPosition = useCallback(() => {
    const anchor = wrapperRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tooltipEl = tooltipRef.current;
    const tooltipHeight = tooltipEl
      ? Math.min(tooltipEl.offsetHeight, TOOLTIP_MAX_HEIGHT)
      : TOOLTIP_MAX_HEIGHT;
    const tooltipWidth = tooltipEl
      ? Math.min(tooltipEl.offsetWidth || TOOLTIP_MAX_WIDTH, TOOLTIP_MAX_WIDTH)
      : TOOLTIP_MAX_WIDTH;

    let left = rect.left;
    left = Math.min(left, vw - tooltipWidth - VIEWPORT_PAD);
    left = Math.max(VIEWPORT_PAD, left);

    const spaceBelow = vh - rect.bottom - TOOLTIP_GAP - VIEWPORT_PAD;
    const spaceAbove = rect.top - TOOLTIP_GAP - VIEWPORT_PAD;
    const placeBelow = spaceBelow >= tooltipHeight || spaceBelow >= spaceAbove;

    let top = placeBelow
      ? rect.bottom + TOOLTIP_GAP
      : rect.top - tooltipHeight - TOOLTIP_GAP;

    top = Math.min(top, vh - tooltipHeight - VIEWPORT_PAD);
    top = Math.max(VIEWPORT_PAD, top);

    setTooltipPos({ top, left });
  }, []);

  const openTooltip = useCallback(() => {
    if (!isTruncated) return;
    if (
      activeTooltipOwnerId !== null
      && activeTooltipOwnerId !== ownerIdRef.current
    ) {
      return;
    }

    clearCloseTimer();
    activeTooltipOwnerId = ownerIdRef.current;
    updateTooltipPosition();
    setTooltipOpen(true);
  }, [clearCloseTimer, isTruncated, updateTooltipPosition]);

  const scheduleCloseTooltip = useCallback((event) => {
    if (event?.relatedTarget && isWithinHoverArea(event.relatedTarget)) {
      clearCloseTimer();
      return;
    }
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      releaseTooltip();
    }, TOOLTIP_CLOSE_DELAY_MS);
  }, [clearCloseTimer, isWithinHoverArea, releaseTooltip]);

  const stopCellAction = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleTooltipEnter = useCallback((event) => {
    event.stopPropagation();
    clearCloseTimer();
  }, [clearCloseTimer]);

  useEffect(() => () => {
    clearCloseTimer();
    if (activeTooltipOwnerId === ownerIdRef.current) {
      activeTooltipOwnerId = null;
    }
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!tooltipOpen) return undefined;
    updateTooltipPosition();
    return undefined;
  }, [tooltipOpen, updateTooltipPosition, value]);

  useEffect(() => {
    if (!tooltipOpen) return undefined;

    const reposition = () => updateTooltipPosition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [tooltipOpen, updateTooltipPosition]);

  if (!hasValue) {
    if (textAs === 'code') {
      return <code className="truncated-copy-cell__code">{renderedText}</code>;
    }
    return <>{renderedText}</>;
  }

  const textProps = {
    className: [
      'truncated-copy-cell__text',
      textAs === 'code' ? 'truncated-copy-cell__text--code' : '',
      textClassName,
    ].filter(Boolean).join(' '),
    ...(isTruncated ? { tabIndex: 0 } : {}),
  };

  const textNode = textAs === 'code'
    ? <code {...textProps}>{renderedText}</code>
    : <span {...textProps}>{renderedText}</span>;

  const tooltip = tooltipOpen && isTruncated
    ? createPortal(
      <span
        ref={tooltipRef}
        className={[
          'truncated-copy-cell__tooltip',
          tooltipWrap ? 'truncated-copy-cell__tooltip--wrap' : '',
        ].filter(Boolean).join(' ')}
        role="tooltip"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          maxWidth: `${TOOLTIP_MAX_WIDTH}px`,
          maxHeight: `${TOOLTIP_MAX_HEIGHT}px`,
        }}
        onMouseEnter={handleTooltipEnter}
        onMouseLeave={scheduleCloseTooltip}
        onMouseDown={stopCellAction}
        onClick={stopCellAction}
      >
        {value}
      </span>,
      document.body,
    )
    : null;

  return (
    <span
      ref={wrapperRef}
      className={[
        'truncated-copy-cell',
        tooltipOpen ? 'truncated-copy-cell--tooltip-open' : '',
      ].filter(Boolean).join(' ')}
      onMouseEnter={openTooltip}
      onMouseLeave={scheduleCloseTooltip}
      onFocus={openTooltip}
      onBlur={scheduleCloseTooltip}
    >
      {textNode}
      <CopyButton
        value={value}
        ariaLabel={copyAriaLabel}
        className="truncated-copy-cell__copy-btn"
      />
      {tooltip}
    </span>
  );
}

export default TruncatedCopyCell;
